// Tiny state-based router for the marketplace remake.
// We skip react-router-dom to keep the surface minimal — one provider,
// one hook, no URL syncing beyond the hash (good enough for the demo).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type RouteName =
  | 'home'
  | 'browse'
  | 'category'
  | 'services'
  | 'service'
  | 'vendor'
  | 'request'
  | 'offers'
  | 'signin'
  | 'signup'
  | 'dashboard'
  | 'messages';

export type RouteState = {
  name: RouteName;
  params?: Record<string, string>;
};

type RouterContextValue = {
  route: RouteState;
  navigate: (name: RouteName, params?: Record<string, string>) => void;
  back: () => void;
  canGoBack: boolean;
};

const RouterContext = createContext<RouterContextValue | null>(null);

// Hash encoding: #home, #category/renovation, #service/atelier-nord-full-reno
function encodeHash(state: RouteState): string {
  const parts: string[] = [state.name];
  if (state.params) {
    const values = Object.values(state.params);
    parts.push(...values);
  }
  return '#' + parts.join('/');
}

function decodeHash(hash: string): RouteState {
  const clean = hash.replace(/^#/, '').trim();
  if (!clean) return { name: 'home' };
  const [name, ...rest] = clean.split('/');
  const known: RouteName[] = [
    'home',
    'browse',
    'category',
    'services',
    'service',
    'vendor',
    'request',
    'offers',
    'signin',
    'signup',
    'dashboard',
    'messages',
  ];
  if (!known.includes(name as RouteName)) return { name: 'home' };

  const params: Record<string, string> = {};
  if (name === 'category' && rest[0]) params.categoryId = rest[0];
  if (name === 'service' && rest[0]) params.serviceId = rest[0];
  if (name === 'vendor' && rest[0]) params.vendorId = rest[0];
  if (name === 'services' && rest[0]) params.categoryId = rest[0];

  return { name: name as RouteName, params };
}

export function MarketplaceRouterProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<RouteState[]>(() => {
    if (typeof window === 'undefined') return [{ name: 'home' }];
    return [decodeHash(window.location.hash)];
  });

  const route = stack[stack.length - 1];

  const navigate = useCallback(
    (name: RouteName, params?: Record<string, string>) => {
      const next: RouteState = { name, params };
      setStack((s) => [...s, next]);
      if (typeof window !== 'undefined') {
        window.location.hash = encodeHash(next);
      }
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    },
    [],
  );

  const back = useCallback(() => {
    setStack((s) => {
      if (s.length <= 1) return s;
      const next = s.slice(0, -1);
      const top = next[next.length - 1];
      if (typeof window !== 'undefined') {
        window.location.hash = encodeHash(top);
      }
      return next;
    });
  }, []);

  // Sync back/forward browser navigation
  useEffect(() => {
    function onHash() {
      const next = decodeHash(window.location.hash);
      setStack((s) => {
        const top = s[s.length - 1];
        if (top.name === next.name && JSON.stringify(top.params) === JSON.stringify(next.params)) {
          return s;
        }
        return [...s, next];
      });
    }
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const value = useMemo<RouterContextValue>(
    () => ({ route, navigate, back, canGoBack: stack.length > 1 }),
    [route, navigate, back, stack.length],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter must be used inside MarketplaceRouterProvider');
  return ctx;
}
